import { Clock, Undo2 } from 'lucide-react'
import styles from './RefundOverview.module.css'
import BlankState from '../BlankState/BlankState'
import { fmtEUR, fmtInt, timeAgo } from '../../../utils/moneyFormat'

// Refund status maps onto the shared pill palette. Anything we don't
// recognise (cancelled, unknown) stays neutral rather than inventing a colour.
const PILL_CLASS = {
  pending: 'dshPillWarn',
  completed: 'dshPillPos',
  refunded: 'dshPillPos',
  failed: 'dshPillNeg',
}

// "Client · ORD-000093 · 2 days ago" — parts that are missing drop out so the
// line never renders a dangling separator.
const metaLine = (refund) =>
  [refund.type, refund.bookingReference, timeAgo(refund.date)]
    .filter(Boolean)
    .join(' · ')

// Refunds issued to clients and received from suppliers. Pending payout is
// called out on its own because that money is still owed to someone.
const RefundOverview = ({ refunds, periodLabel, loading }) => {
  const recent = refunds?.recent || []
  const pendingCount = Number(refunds?.pendingCount) || 0

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div>
          <h3 className="dshTitle">Refunds</h3>
          {/* Paid-out follows the period; pending payout is a standing debt
              and deliberately does not. */}
          <p className="dshSub dshTrunc">
            Paid in {periodLabel || 'this period'} &amp; awaiting payout
          </p>
        </div>
      </div>

      <div className={styles.figures}>
        <div className={styles.figure}>
          <div className={styles.figureHead}>
            <span className="dshEyebrow">Paid out</span>
          </div>
          {loading ? (
            <span className={`dshSkel ${styles.skelFigure}`} />
          ) : (
            <strong className={`dshNum ${styles.figureValue}`}>
              {fmtEUR(refunds?.completedInPeriod)}
            </strong>
          )}
        </div>

        <div className={styles.figure}>
          <div className={styles.figureHead}>
            <span className="dshEyebrow">Pending payout</span>
            {!loading && pendingCount > 0 && (
              <span className="dshPill dshPillWarn dshNum">
                <Clock size={11} strokeWidth={2.2} aria-hidden="true" />
                {fmtInt(pendingCount)} open
              </span>
            )}
          </div>
          {loading ? (
            <span className={`dshSkel ${styles.skelFigure}`} />
          ) : (
            <strong className={`dshNum ${styles.figureValue} ${styles.figureWarn}`}>
              {fmtEUR(refunds?.pendingAmount)}
            </strong>
          )}
        </div>
      </div>

      <div className="dshBody">
        <span className={`dshEyebrow ${styles.sectionLabel}`}>Latest refunds</span>

        <div className={styles.list}>
          {loading &&
            [0, 1, 2].map((i) => (
              <div key={i} className="dshRow">
                <div className={styles.rowMain}>
                  <span className={`dshSkel ${styles.skelName}`} />
                  <span className={`dshSkel ${styles.skelMeta}`} />
                </div>
                <div className={styles.rowSide}>
                  <span className={`dshSkel ${styles.skelAmount}`} />
                  <span className={`dshSkel ${styles.skelPill}`} />
                </div>
              </div>
            ))}

          {!loading && recent.length === 0 && (
            <BlankState
              icon={Undo2}
              title="No refunds yet"
              text="Refunds issued to clients and those still awaiting payout will be listed here."
            />
          )}

          {!loading &&
            recent.map((r) => {
              const status = r.status || 'unknown'
              return (
                <div key={r.id} className="dshRow">
                  <div className={styles.rowMain}>
                    <span className={`dshTrunc ${styles.name}`}>{r.name}</span>
                    <span className={`dshTrunc ${styles.meta}`}>{metaLine(r)}</span>
                  </div>
                  <div className={styles.rowSide}>
                    <span className={`dshNum ${styles.amount}`}>{fmtEUR(r.amount)}</span>
                    <span
                      className={`dshPill ${PILL_CLASS[status] || 'dshPillMute'} ${styles.status}`}
                    >
                      {status}
                    </span>
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default RefundOverview
